import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Actions from 'actions'
import { AsyncStatus } from 'consts'
import { TimelineItemTypes } from 'consts/timelineConsts'
import { buildMissedScanItems } from 'utils/timelineCyclesUtils'

/**
 * The items the time-based mini timeline draws: the patient's scans, plus the scans that were due
 * and never arrived.
 *
 * Month labels are not included - the time-based layout draws every month between the first scan
 * and today, including the ones that hold nothing, rather than only the months that have items.
 *
 * Cycles are fetched only for practices on the flag, so patients on the scan-based timeline pay
 * nothing for this.
 *
 * @param miniTimelineItems the items built from the patient's TimelineItems.
 * @param isEnabled whether the time-based timeline is the active layout.
 */
export default ({ miniTimelineItems = [], isEnabled = false }) => {
  const dispatch = useDispatch()

  // `requestedPatientId` is cleared once the patient has loaded, so on its own it only catches the
  // case where this view was already selected at page load. Picking the view later has to work too.
  const patientId = useSelector(state => state.patientsReducer.requestedPatientId || state.patientsReducer.patient?.id)
  const { data: cycles, loadStatus: cyclesLoadStatus } = useSelector(state => state.timelineReducer.miniTimeline.cycles)
  // The treatment's interval, and when it last changed, is what dates the stretch a rewritten cycle
  // can no longer speak for.
  // Guarded against the patient the cycles were asked for, not the one that has finished loading:
  // during a switch those are two different people for a while, and the treatment lives in its own
  // slice, cleared on its own schedule. Comparing against the loaded patient accepts the previous
  // one's treatment and pairs it with the new one's cycles if the cycles arrive first.
  const treatment = useSelector(state =>
    state.treatmentReducer.treatment?.patientId === patientId ? state.treatmentReducer.treatment : null
  )

  useEffect(() => {
    if (isEnabled && patientId) {
      dispatch(Actions.fetchPatientCycles({ patientId }))
    }
  }, [isEnabled, patientId, dispatch])

  const scanItems = useMemo(
    () => miniTimelineItems.filter(item => item.type === TimelineItemTypes.GrinScan),
    [miniTimelineItems]
  )

  const missedScanItems = useMemo(
    () => (isEnabled ? buildMissedScanItems({ cycles, treatment }) : []),
    [isEnabled, cycles, treatment]
  )

  const timeBasedItems = useMemo(() => [...scanItems, ...missedScanItems], [scanItems, missedScanItems])

  return {
    timeBasedItems,
    // An axis with no hollow markers reads as a patient who missed nothing, so a failed fetch has
    // to say so rather than pass for a clean record.
    missedScansUnavailable: cyclesLoadStatus === AsyncStatus.Failed
  }
}
